import type { FlowChart as FlowChartData } from '@curriculum/types';

type Props = {
  chart: FlowChartData;
};

/**
 * FlowChart — a short, kid-friendly diagram for a theory topic.
 *
 * Four shapes (see FlowChartSchema in curriculum/types.ts):
 * - sequence: numbered steps joined by arrows, read top to bottom.
 * - cycle: the same steps, plus a note that it starts again at step 1.
 * - branch: a question, then each option as "If … → …".
 * - tree: a root idea with grouped items underneath.
 *
 * Plain HTML and CSS only. Lists stay real lists, so screen readers and phones
 * get the same content; the arrows and step badges are decorative (aria-hidden).
 */
export function FlowChart({ chart }: Props) {
  return (
    <section aria-label={chart.title} className="rounded-card-lg border border-ink-200/80 bg-cream shadow-card p-5 sm:p-7">
      <h3 className="font-serif text-xl sm:text-[1.4rem] text-ink-900 tracking-tight text-balance">{chart.title}</h3>
      {chart.caption && <p className="mt-1 text-sm leading-relaxed text-ink-600">{chart.caption}</p>}
      <div className="mt-5">
        {chart.kind === 'sequence' && <Steps steps={chart.steps} />}
        {chart.kind === 'cycle' && (
          <>
            <Steps steps={chart.steps} />
            <p className="mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-accent-600">
              <span aria-hidden="true">↺</span>
              The cycle repeats: go back to step 1.
            </p>
          </>
        )}
        {chart.kind === 'branch' && <Branch question={chart.question} options={chart.options} />}
        {chart.kind === 'tree' && <Tree root={chart.root} groups={chart.groups} />}
      </div>
    </section>
  );
}

function Steps({ steps }: { steps: { label: string; note?: string }[] }) {
  return (
    <ol className="flex flex-col">
      {steps.map((step, i) => (
        <li key={i} className="flex flex-col">
          <div className="flex gap-4 rounded-xl border border-ink-200/80 bg-gradient-to-br from-cream to-ink-50 p-4 shadow-soft">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-sm font-semibold text-ink-900 ring-4 ring-accent-50 shadow-btn"
            >
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="font-semibold leading-snug text-ink-900">{step.label}</p>
              {step.note && <p className="mt-1 text-sm leading-relaxed text-ink-600">{step.note}</p>}
            </div>
          </div>
          {i < steps.length - 1 && (
            <span aria-hidden="true" className="mx-auto py-1 text-lg leading-none text-accent-600">↓</span>
          )}
        </li>
      ))}
    </ol>
  );
}

function Branch({
  question,
  options,
}: {
  question: string;
  options: { condition: string; outcome: string; note?: string }[];
}) {
  return (
    <div>
      <p className="aces-band-night rounded-xl bg-night-700 px-4 py-3.5 text-center font-semibold text-white shadow-card">{question}</p>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {options.map((option, i) => (
          <li key={i} className="rounded-xl border border-ink-200/80 bg-ink-50 p-4 shadow-soft">
            <p className="text-sm text-ink-600">
              If <span className="font-semibold text-ink-900">{option.condition}</span>
            </p>
            <p className="mt-2 font-semibold text-accent-600">
              <span aria-hidden="true">→ </span>
              {option.outcome}
            </p>
            {option.note && <p className="mt-1 text-sm leading-relaxed text-ink-600">{option.note}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Tree({ root, groups }: { root: string; groups: { heading: string; items: string[] }[] }) {
  return (
    <div>
      <p className="rounded-xl bg-accent-500 px-4 py-3.5 text-center font-semibold text-ink-900 shadow-btn">{root}</p>
      <span aria-hidden="true" className="mx-auto block h-5 w-px bg-ink-200" />
      <ul className="grid gap-3 sm:grid-cols-2">
        {groups.map((group, i) => (
          <li key={i} className="rounded-xl border border-ink-200/80 bg-ink-50 p-4 shadow-soft">
            <p className="font-semibold text-ink-900">{group.heading}</p>
            <ul className="mt-2 space-y-1.5 text-sm text-ink-700">
              {group.items.map((item, j) => (
                <li key={j} className="flex gap-2">
                  <span aria-hidden="true" className="text-accent-600">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
